import { AddressJsonldAddressWrite, AddressValidatedJsonldAddressValidatedPostWrite } from '@/api/generated';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import { useMemo } from 'react';
import { addressesFactory } from 'utils/factories/addresses';
import { type ApiClient } from 'api/client';

export type AddressesFilters = Partial<Parameters<ApiClient['addresses']['apiAddressesGetCollection']>[0]>;

export const useUserAddresses = (filters?: AddressesFilters) => {
  const apiClient = useApi();

  const { data: addresses, isLoading } = useQuery({
    queryKey: addressesFactory.list({ ...(filters || {}) }),
    queryFn: () => apiClient.addresses.apiAddressesGetCollection(filters).then((v) => v.data['hydra:member']),
  });

  const defaultBillingAddress = useMemo(() => addresses?.find((address) => address.isDefaultBilling), [addresses]);
  const defaultShippingAddress = useMemo(() => addresses?.find((address) => address.isDefaultShipping), [addresses]);

  return {
    addresses,
    isLoading,
    defaultBillingAddress,
    defaultShippingAddress,
  };
};

export const useCreateAddress = () => {
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: addressesFactory.all() });
  };

  const apiClient = useApi();
  const validateAddress = useMutation({
    mutationKey: ['validateAddress'],
    mutationFn: async ({ values, countryCode, zipCode }: { values: string; countryCode: string; zipCode: string }) =>
      apiClient.validateAddresses.apiValidateAddressesPost({ entered: values, countryCode, zipCode }),
  });

  const createAddress = useMutation({
    mutationKey: ['createAddress'],
    mutationFn: async ({
      data,
      responseId,
    }: {
      data: AddressValidatedJsonldAddressValidatedPostWrite;
      responseId: string;
    }) => apiClient.validateAddresses.apiValidateAddressesResponseIdPost(data, responseId),
    onSuccess,
  });

  return { validateAddress, createAddress };
};

export const usePutAddress = () => {
  const queryClient = useQueryClient();
  const apiClient = useApi();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: addressesFactory.all() });
  };
  const updateAddress = useMutation({
    mutationKey: ['updateAddress'],
    mutationFn: async ({ id, update }: { id: string; update: AddressJsonldAddressWrite }) =>
      apiClient.addresses.apiAddressesIdPut(id, update),
    onSuccess,
  });

  return { updateAddress };
};
